import { useId, type ReactElement } from 'react'

/**
 * The five bespoke IELTS skill marks, inlined so they never depend on a
 * network fetch, an icon font or `currentColor`. The hues ARE the wayfinding
 * (Listening lavender, Reading mint, Writing sun, Speaking coral, Full mock
 * ink), so these marks deliberately do not recolour, and nothing should place
 * a coloured plate behind them — the mark carries its own silhouette.
 *
 * Geometry is copied verbatim from the approved 48×48 source SVGs. Do not
 * redraw: the facets, radii and gaps were tuned to survive 20px rendering.
 */
export type SkillIconName = 'listening' | 'reading' | 'writing' | 'speaking' | 'full'

const SINGLE_SKILLS: ReadonlyArray<SkillIconName> = ['listening', 'reading', 'writing', 'speaking']

/**
 * Which mark represents a set of canonical section types. Case-insensitive, so
 * the capitalised `SectionType` and the lowercase section-score keys resolve
 * through one rule. Anything that is neither a complete four-paper mock nor a
 * single paper gets no mark at all rather than a guessed one.
 */
export function skillForSections(sections: readonly string[]): SkillIconName | null {
  const present = SINGLE_SKILLS.filter((skill) => sections.some((entry) => entry.trim().toLowerCase() === skill))
  if (present.length === 4) return 'full'
  return present.length === 1 ? present[0]! : null
}

interface SkillIconProps {
  skill: SkillIconName
  /** Rendered edge length in px; 20 is the legibility floor, 64 the drawn-for size. */
  size?: number
  /** Supply only when the mark is the sole carrier of meaning — otherwise it stays decorative. */
  label?: string
}

/**
 * Two of the marks clip flat colour blocks into a single silhouette, which
 * needs a document-unique id per rendered instance or the first definition
 * would win for every later copy on the page.
 */
function Mark({ skill, clipId }: { skill: SkillIconName; clipId: string }): ReactElement {
  switch (skill) {
    case 'listening':
      return (
        <>
          <path fill="#B9A7F0" d="M6 30V24.5a18 18 0 0 1 36 0v5.5h-7.5v-5.5a10.5 10.5 0 0 0-21 0v5.5z" />
          <path fill="#C9BBF4" d="M6 30V24.5a18 18 0 0 1 36 0v5.5h-2.5v-5.5a15.5 15.5 0 0 0-31 0v5.5z" />
          <rect x="4.5" y="21.5" width="13.5" height="20" rx="6.75" fill="#B9A7F0" />
          <rect x="30" y="21.5" width="13.5" height="20" rx="6.75" fill="#B9A7F0" />
          <rect x="7.75" y="25" width="7" height="13" rx="3.5" fill="#26215C" />
          <rect x="33.25" y="25" width="7" height="13" rx="3.5" fill="#26215C" />
        </>
      )
    case 'reading':
      return (
        <>
          <g strokeWidth="7" strokeLinejoin="round" strokeLinecap="round">
            <path fill="#A9EBD6" stroke="#A9EBD6" d="M8.5 11 20.5 15v15H8.5Z" />
            <path fill="#63D9B4" stroke="#63D9B4" d="M39.5 11 27.5 15v15h12Z" />
            <path fill="#04342C" stroke="#04342C" d="M8 31.5q16 2.5 32 0v1.5q-16 2.5-32 0z" />
          </g>
          <rect x="21.5" y="15" width="5" height="18" rx="2.5" fill="#04342C" />
        </>
      )
    case 'writing':
      return (
        <>
          <defs>
            <clipPath id={clipId}>
              <path d="M13.55 13.15a10.45 10.45 0 0 1 20.9 0v22.14q0 3.08-1.99 5.42l-6.7 7.88q-1.76 2.07-3.52 0l-6.7-7.88Q13.55 38.36 13.55 35.29Z" />
            </clipPath>
          </defs>
          <g transform="rotate(45 24 24)">
            <g clipPath={`url(#${clipId})`}>
              <rect x="-24" y="-24" width="96" height="96" fill="#F6E27A" />
              <rect x="18" y="-24" width="5" height="65.8" fill="#FFFFFF" />
              <rect x="-24" y="-24" width="96" height="35.5" fill="#633806" />
              <rect x="-24" y="41.8" width="96" height="30" fill="#633806" />
            </g>
          </g>
        </>
      )
    case 'speaking':
      return (
        <>
          <defs>
            <clipPath id={clipId}>
              <rect x="6" y="6" width="36" height="26" rx="9.5" />
              <path d="M18.5 24.5 29 30 15.4 41.4Q13.3 43.2 14.1 40.4Z" />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            <rect x="0" y="0" width="48" height="48" fill="#E8532C" />
            <rect x="0" y="28" width="48" height="20" fill="#8C2E14" />
          </g>
          <rect x="12.5" y="11.5" width="23" height="5" rx="2.5" fill="#F1EFE8" />
          <rect x="12.5" y="20" width="15" height="5" rx="2.5" fill="#F1EFE8" />
        </>
      )
    case 'full':
      return (
        <>
          <rect x="17" y="3" width="14" height="11" rx="5" fill="#171513" />
          <rect x="8.5" y="9" width="31" height="34" rx="7.5" fill="#171513" />
          <rect x="20.5" y="5.5" width="7" height="4" rx="2" fill="#F1EFE8" />
          <rect x="13" y="17.5" width="9" height="9" rx="2" fill="#B9A7F0" />
          <rect x="26" y="17.5" width="9" height="9" rx="2" fill="#63D9B4" />
          <rect x="13" y="30.5" width="9" height="9" rx="2" fill="#F6E27A" />
          <rect x="26" y="30.5" width="9" height="9" rx="2" fill="#E8532C" />
        </>
      )
  }
}

export function SkillIcon({ skill, size = 32, label }: SkillIconProps) {
  // React ids carry delimiters that are not safe inside a `url(#…)` reference.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const titleId = `skill-icon-${uid}-title`
  const naming = label
    ? ({ role: 'img', 'aria-labelledby': titleId } as const)
    : ({ 'aria-hidden': true } as const)

  return (
    <svg
      className="skill-icon"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      // Inline sizing so an inherited descendant `svg` rule can never stretch
      // the mark and shift the layout around it.
      style={{ width: size, height: size }}
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      {...naming}
    >
      {label ? <title id={titleId}>{label}</title> : null}
      <Mark skill={skill} clipId={`skill-icon-${uid}-clip`} />
    </svg>
  )
}
